import React from "react";
import { useNavigate } from "react-router-dom";

// Data for the projects (Id, Title, Description, Live URL)
// You can test by changing this array length to 1, 2, 3, etc.
const projectData = [
  {
    id: 1,
    title: "BunkMates",
    description:
      "A complete travel and trip-management ecosystem designed to make group travel easier, smarter, and more social. Includes a modern landing page, full-featured web application, and dedicated admin panel for managing users, trips, communities, notifications, releases, analytics, and platform settings. The application brings together trip planning, private and group messaging, trip invites, budget and expense tracking, to-do lists, notes, media and voice notes, nearby attractions, weather updates, calendars, offline support, PWA capabilities, reminders, and cross-device data synchronization.",
    liveUrl: "https://bunkmates.xyz",
  },

  {
    id: 2,
    title: "ANVI Library & Apartments",
    description:
      "A complete web platform for ANVI Library & Apartments, featuring a user-facing website for exploring the property, facilities, accommodation, library services, and related information, along with a dedicated admin panel for managing the platform's content and operations.",
    liveUrl: "https://anvi-library-apartment.vercel.app/",
  },

  {
    id: 3,
    title: "J. Alex Salon Web",
    description:
      "A premium responsive salon website designed for J. Alex Salon, featuring an elegant digital presence for bridal makeup, beauty services, hair styling, skin treatments, cinematic beauty reels, salon information, contact details, and appointment booking. The website combines a luxury editorial aesthetic with responsive experiences across desktop and mobile devices.",
    liveUrl: "https://j-alex-salon.vercel.app/",
  },

  {
    id: 4,
    title: "BuildX Custom Design & Dev Hackathon",
    description:
      "A custom-designed and developed hackathon website built to create an engaging digital experience for BuildX. The platform combines a modern event-focused interface with structured hackathon information, participant-oriented sections, and a distinctive visual identity designed to support the event's digital presence and developer community.",
    liveUrl: "https://buildx.bunkmates.xyz/",
  },
];

function ProjectCard({ id, isSingle }) {
  const project = projectData.find((p) => p.id === id) || {
    id,
    title: "Unknown Project",
    description: "No description available.",
    liveUrl: "#",
  };

  return (
    <div className={`project-card ${isSingle ? "single-layout" : ""}`}>
      <div className="project-image-wrapper">
        <img
          src={`/assets/projects/${id}.jpeg`}
          alt={project.title}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <span className="project-number">{`#0${id}`} — WYT</span>
      </div>

      <div className="project-info">
        <div>
          <h3 className="project-title">{project.title}</h3>
          <p className="project-description">{project.description}</p>
        </div>

        {/* Live Website Link Button */}
        <div className="project-footer">
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="live-website-btn"
          >
            Live Website
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="7" y1="17" x2="17" y2="7"></line>
              <polyline points="7 7 17 7 17 17"></polyline>
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Parallax() {
  const navigate = useNavigate();

  // Slice to show first 6 projects (or change count as needed)
  const displayedProjects = projectData.slice(0, 6);
  const totalCount = displayedProjects.length;

  return (
    <div className="projects-container-wrapper">
      <h1 className="projects-main-title">Our Projects</h1>
      <div id="project-grid-example" className={`grid-count-${totalCount}`}>
        {displayedProjects.map((project, index) => {
          const remainder = totalCount % 3;
          const isLastRowRemainder =
            index >= totalCount - remainder && remainder > 0;

          return (
            <div
              key={project.id}
              className={`project-card-wrapper ${
                isLastRowRemainder ? "remainder-card" : ""
              }`}
            >
              <ProjectCard id={project.id} isSingle={totalCount === 1} />
            </div>
          );
        })}
      </div>

      {/* View All Projects Button */}
      <div className="view-all-container">
        <button
          onClick={() => navigate("/projects")}
          className="view-all-projects-btn"
        >
          View all projects
        </button>
      </div>

      <StyleSheet />
    </div>
  );
}

/**
 * ==============   Styles (Dynamic Grid & Off-White/Brown Theme)    ================
 */

function StyleSheet() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');

      .projects-container-wrapper {
        --ink: #2B211C;
        --ink-soft: #5F5148;
        --accent: #A06E50;
        --cream: #F7F4EF;
        --surface: #FCFAF6;
        --line: rgba(43, 33, 28, 0);
        --line-soft: rgba(43, 33, 28, 0.09);

        position: relative;
        width: 100%;
        min-height: 100vh;
        box-sizing: border-box;
        padding: clamp(72px, 9vw, 138px) clamp(18px, 4vw, 64px) 110px;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
        background: transparent;
        color: var(--ink);
      }

      .projects-container-wrapper::before {
        content: "";
        position: absolute;
        top: 0;
        left: 50%;
        width: min(1180px, 92vw);
        height: 1px;
        transform: translateX(-50%);
        background: var(--line);
      }

      .projects-heading {
        width: min(1180px, 100%);
        margin: 0 auto clamp(42px, 6vw, 76px);
      }

      .projects-eyebrow {
        display: flex;
        align-items: center;
        gap: 11px;
        width: fit-content;
        margin-bottom: 22px;
        color: var(--accent);
        font-size: 0.66rem;
        font-weight: 800;
        line-height: 1;
        letter-spacing: 0.16em;
        text-transform: uppercase;
      }

      .projects-eyebrow-dot {
        width: 7px;
        height: 7px;
        flex: 0 0 7px;
        border-radius: 50%;
        background: var(--accent);
      }

      .projects-eyebrow-line {
        width: 44px;
        height: 1px;
        margin-left: 3px;
        background: var(--accent);
        opacity: 0.55;
      }

      .projects-main-title {
        margin: 0;
        max-width: 920px;
        color: var(--ink);
        font-size: clamp(3.4rem, 8vw, 7.6rem);
        font-weight: 780;
        line-height: 0.86;
        letter-spacing: -0.075em;
      }

      .projects-main-title span {
        display: block;
        color: var(--accent);
      }

      .projects-main-description {
        max-width: 570px;
        margin: 28px 0 0;
        color: var(--ink-soft);
        font-size: clamp(0.9rem, 1.2vw, 1.02rem);
        line-height: 1.7;
        letter-spacing: -0.01em;
      }

      #project-grid-example {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: clamp(14px, 2vw, 24px);
        width: min(1180px, 100%);
        margin: 0 auto;
      }

      #project-grid-example.grid-count-1 {
        grid-template-columns: minmax(0, 850px);
        justify-content: center;
      }

      #project-grid-example.grid-count-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        width: min(900px, 100%);
      }

      .project-card-wrapper {
        min-width: 0;
      }

      .project-card {
        position: relative;
        height: 100%;
        min-height: 515px;
        box-sizing: border-box;
        padding: 10px 10px 18px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        border: 1px solid var(--line);
        border-radius: 24px;
        background: var(--surface);
        box-shadow: 0 8px 28px rgba(43, 33, 28, 0.035);
        transition:
          transform 420ms cubic-bezier(.22, 1, .36, 1),
          box-shadow 420ms cubic-bezier(.22, 1, .36, 1),
          border-color 280ms ease;
      }

      .project-card::after {
        content: "";
        position: absolute;
        right: 18px;
        bottom: 18px;
        width: 58px;
        height: 58px;
        border-right: 1px solid var(--line-soft);
        border-bottom: 1px solid var(--line-soft);
        pointer-events: none;
      }

      .project-card:hover {
        transform: translateY(-8px);
        border-color: rgba(160, 110, 80, 0.42);
        box-shadow: 0 24px 60px rgba(43, 33, 28, 0.10);
      }

      .project-card.single-layout {
        flex-direction: row;
        align-items: stretch;
        gap: clamp(24px, 4vw, 48px);
        min-height: 420px;
        padding: 14px;
      }

      .project-card.single-layout .project-image-wrapper {
        width: 52%;
        height: auto;
        min-height: 390px;
        flex-shrink: 0;
      }

      .project-card.single-layout .project-info {
        padding: 18px 20px 20px 0;
      }

      .project-image-wrapper {
        position: relative;
        width: 100%;
        height: 270px;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 18px;
        background: #E9E1D7;
        border: 0px solid var(--line-soft);
      }

      .project-image-wrapper::after {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: rgba(43, 33, 28, 0.04);
        transition: background 300ms ease;
      }

      .project-card:hover .project-image-wrapper::after {
        background: rgba(43, 33, 28, 0);
      }

      .project-card img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 700ms cubic-bezier(.22, 1, .36, 1);
      }

      .project-card:hover img {
        transform: scale(1.045);
      }

      .project-number {
        position: absolute;
        z-index: 2;
        top: 11px;
        right: 15px;
        font-family: 'monospace', sans-serif;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 42px;
        height: 28px;
        box-sizing: border-box;
        padding: 0 9px;
        border: 0px solid rgba(255, 253, 249, 0.28);
        border-radius: 999px;
        background: rgba(43, 33, 28, 0.21);
        color: #FFFDF9;
        font-size: 0.61rem;
        font-weight: 800;
        letter-spacing: 0.12em;
        backdrop-filter: blur(8px);
      }

      .project-info {
        position: relative;
        z-index: 2;
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: space-between;
        padding: 22px 10px 0;
        text-align: left;
      }

      .project-title {
        margin: 0 0 10px;
        color: var(--ink);
        font-size: clamp(1.28rem, 1.8vw, 1.7rem);
        font-weight: 780;
        line-height: 1.05;
        letter-spacing: -0.045em;
      }

      .project-description {
        display: -webkit-box;
        margin: 0;
        overflow: hidden;
        color: var(--ink-soft);
        font-family: inherit;
        font-size: 0.78rem;
        line-height: 1.65;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
      }

      .project-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 26px;
        padding-top: 16px;
        border-top: 1px solid var(--line-soft);
      }

      .project-footer::before {
        content: "Visit project";
        color: var(--ink-soft);
        font-size: 0.61rem;
        font-weight: 800;
        letter-spacing: 0.11em;
        text-transform: uppercase;
      }

      .live-website-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 40px;
        box-sizing: border-box;
        padding: 0 14px;
        border: 0px solid var(--ink);
        border-radius: 999px;
        background: var(--ink);
        color: #FFFDF9;
        font-size: 0.68rem;
        font-weight: 750;
        letter-spacing: 0.01em;
        text-decoration: none;
        transition:
          background-color 220ms ease,
          color 220ms ease,
          transform 220ms ease,
          border-color 220ms ease;
      }

      .live-website-btn:hover {
        transform: translateY(-2px);
        border-color: var(--accent);
        background: var(--accent);
        color: #FFFDF9;
      }

      .view-all-container {
        width: min(1180px, 100%);
        margin-top: clamp(40px, 5vw, 64px);
        padding-top: 22px;
        display: flex;
        justify-content: flex-end;
        border-top: 1px solid var(--line);
      }

      .view-all-projects-btn {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        min-height: 48px;
        padding: 0 4px 0 16px;
        border: 0;
        border-bottom: 1px solid var(--ink);
        border-radius: 0;
        color: var(--ink);
        background: transparent;
        cursor: pointer;
        font-family: inherit;
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        transition:
          color 220ms ease,
          border-color 220ms ease,
          gap 220ms ease;
      }

      .view-all-projects-btn::after {
        content: "↗";
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 1px solid currentColor;
        border-radius: 50%;
        font-size: 0.82rem;
        line-height: 1;
      }

      .view-all-projects-btn:hover {
        gap: 17px;
        color: var(--accent);
        border-color: var(--accent);
        background: transparent;
        transform: none;
      }

      @media (max-width: 1050px) {
        #project-grid-example,
        #project-grid-example.grid-count-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          width: 100%;
        }

        .project-card {
          min-height: 500px;
        }
      }

      @media (max-width: 760px) {
        .projects-container-wrapper {
          padding: 68px 16px 80px;
        }

        .projects-heading {
          margin-bottom: 40px;
        }

        .projects-main-title {
          font-size: clamp(3.15rem, 16vw, 5rem);
        }

        .projects-main-description {
          max-width: 430px;
          margin-top: 22px;
          font-size: 0.84rem;
        }

        #project-grid-example,
        #project-grid-example.grid-count-2,
        #project-grid-example.grid-count-1 {
          grid-template-columns: 1fr;
          width: 100%;
        }

        .project-card {
          min-height: 0;
          padding: 8px 8px 15px;
          border-radius: 20px;
        }

        .project-image-wrapper {
          height: 270px;
          border-radius: 15px;
        }

        .project-info {
          padding: 18px 8px 0;
        }

        .project-description {
          -webkit-line-clamp: 5;
        }

        .project-card.single-layout {
          flex-direction: column;
          gap: 0;
          min-height: 0;
        }

        .project-card.single-layout .project-image-wrapper {
          width: 100%;
          min-height: 225px;
          height: 225px;
        }

        .project-card.single-layout .project-info {
          padding: 18px 8px 0;
        }

        .view-all-container {
          justify-content: flex-start;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .project-card,
        .project-card img,
        .live-website-btn,
        .view-all-projects-btn {
          transition: none !important;
        }

        .project-card:hover,
        .live-website-btn:hover {
          transform: none;
        }

        .project-card:hover img {
          transform: none;
        }
      }
    `}</style>
  );
}